import React, { useRef, useState } from 'react';
import api from '../../services/api';
import { GOV_COLORS as C, S } from './govShared';

export const MAX_GOV_FILE_BYTES = 100 * 1024 * 1024;
const COST_EXTENSIONS = /\.(pdf|xlsx|xls|xlsm|csv|tsv|docx|doc|txt|ods)$/i;

interface Props {
  solicitationId: string;
  costAnalysis?: boolean;
  onUploaded: () => void | Promise<void>;
}

/** Shared file picker/drop target. Retains originals; never queues AI processing. */
export default function GovFileUpload({ solicitationId, costAnalysis = false, onUploaded }: Props) {
  const input = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const depth = useRef(0);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<string[]>([]);

  const upload = async (files: File[]) => {
    if (busy.current || !files.length) return;
    busy.current = true;
    setErrors([]);
    setMessage('');
    const failures: string[] = [];
    let completed = 0;
    try {
      for (let index = 0; index < files.length; index++) {
        const file = files[index];
        setProgress(`Uploading ${index + 1} of ${files.length}…`);
        if (!file.size || file.size > MAX_GOV_FILE_BYTES) {
          failures.push(`${file.name}: choose a non-empty file up to 100 MB.`);
          continue;
        }
        if (costAnalysis && !COST_EXTENSIONS.test(file.name)) {
          failures.push(`${file.name}: choose a spreadsheet, PDF, Word document, or text file.`);
          continue;
        }
        const body = new FormData();
        body.append('file', file);
        if (costAnalysis) {
          body.append('category', 'internal');
          body.append('file_type', 'cost_analysis');
        }
        try {
          await api.post(`/gov/solicitations/${solicitationId}/documents`, body, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          completed++;
        } catch (e: any) {
          failures.push(`${file.name}: ${e?.response?.data?.error || 'Upload failed. Try this file again.'}`);
        }
      }
      if (completed) {
        setMessage(`${completed} ${completed === 1 ? 'file uploaded' : 'files uploaded'}.`);
        try { await onUploaded(); } catch { failures.push('Files saved, but the list could not refresh. Reload to see them.'); }
      }
    } finally {
      setErrors(failures);
      setProgress('');
      busy.current = false;
    }
  };

  return (
    <section aria-label={costAnalysis ? 'Cost analysis upload' : 'Document upload'}
      onDragEnter={e => { e.preventDefault(); if (e.dataTransfer.types.includes('Files')) { depth.current++; setDragging(true); } }}
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = busy.current ? 'none' : 'copy'; }}
      onDragLeave={e => { e.preventDefault(); if (--depth.current <= 0) { depth.current = 0; setDragging(false); } }}
      onDrop={e => { e.preventDefault(); depth.current = 0; setDragging(false); void upload(Array.from(e.dataTransfer.files)); }}
      style={{ ...S.card, marginBottom: 16, padding: 20, border: `1px dashed ${dragging ? C.accent : '#2D4259'}`, background: dragging ? C.cardHi : C.card }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 220px' }}>
          <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>{dragging ? 'Drop files here' : costAnalysis ? 'Upload your own cost analysis' : 'Drag and drop documents here'}</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 5 }}>
            {costAnalysis ? 'Spreadsheets, PDFs, Word documents, or text · 100 MB per file' : 'Choose or drop multiple files · 100 MB per file'}
          </div>
        </div>
        <input ref={input} type="file" multiple hidden aria-label={costAnalysis ? 'Choose cost analysis files' : 'Choose documents'}
          accept={costAnalysis ? '.pdf,.xlsx,.xls,.xlsm,.csv,.tsv,.docx,.doc,.txt,.ods' : undefined}
          onChange={e => { const files = Array.from(e.target.files || []); e.target.value = ''; void upload(files); }} />
        <button type="button" style={{ ...S.addBtn, opacity: progress ? 0.6 : 1 }} disabled={!!progress}
          onClick={() => input.current?.click()}>{progress || (costAnalysis ? 'Upload Cost Analysis' : 'Upload Documents')}</button>
      </div>
      {(progress || message) && <div role="status" style={{ color: C.textMuted, fontSize: 13, marginTop: 12 }}>{progress || message}</div>}
      {errors.length > 0 && <div role="alert" style={{ color: C.danger, fontSize: 13, marginTop: 12 }}>
        {errors.map((error, index) => <div key={index}>{error}</div>)}
      </div>}
    </section>
  );
}
